import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Menu, Stack, SvgIcon, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';

import ScrollArea from '@/shared/ui/ScrollArea';

import { CAMPAIGN_PERIOD_FIELD_ID } from './GeneratikTable.constants';
import {
  ColumnCheckbox,
  ColumnMenuItem,
  ToolbarButton,
  menuPaperProps,
} from './GeneratikTableToolbar.styles';

export interface ToolbarColumnsMenuProps {
  columns: GridColDef[];
  visibleColumns: Record<string, boolean>;
  campaignColumnVisibility?: Record<string, boolean>;
  onColumnToggle: (columnId: string) => void;
  onDisabledItemClick?: (fieldId: string) => void;
  collapsed?: boolean;
  disabled?: boolean;
}

const ToolbarColumnsMenu = ({
  columns,
  visibleColumns,
  campaignColumnVisibility,
  onColumnToggle,
  onDisabledItemClick,
  collapsed = false,
  disabled = false,
}: ToolbarColumnsMenuProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleToggle = useCallback(
    (columnId: string) => {
      onColumnToggle(columnId);
    },
    [onColumnToggle],
  );

  const handleDisabledItemClick = useCallback(() => {
    handleClose();
    onDisabledItemClick?.(CAMPAIGN_PERIOD_FIELD_ID);
  }, [handleClose, onDisabledItemClick]);

  return (
    <>
      <Tooltip title={collapsed ? t('Columns') : ''} arrow disableHoverListener={!collapsed}>
        <ToolbarButton
          collapsed={collapsed}
          startIcon={
            <SvgIcon viewBox="0 0 16 16" sx={{ width: '1rem', height: '1rem' }}>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.6667 1.5C13.6792 1.5 14.5 2.32081 14.5 3.33333V12.6667C14.5 13.6792 13.6792 14.5 12.6667 14.5H3.33333C2.32081 14.5 1.5 13.6792 1.5 12.6667V3.33333C1.5 2.32081 2.32081 1.5 3.33333 1.5H12.6667ZM3.33333 2.5C2.8731 2.5 2.5 2.8731 2.5 3.33333V12.6667C2.5 13.1269 2.8731 13.5 3.33333 13.5H5.5V2.5H3.33333ZM10.5 13.5H12.6667C13.1269 13.5 13.5 13.1269 13.5 12.6667V3.33333C13.5 2.8731 13.1269 2.5 12.6667 2.5H10.5V13.5ZM6.5 13.5H9.5V2.5H6.5V13.5Z"
                fill="currentColor"
              />
            </SvgIcon>
          }
          endIcon={<KeyboardArrowDownIcon />}
          onClick={handleClick}
          disabled={disabled}
          data-testid="columns-menu-button"
        >
          <span className="button-text">{t('Columns')}</span>
        </ToolbarButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              ...menuPaperProps.sx,
              minWidth: '11.25rem',
              overflow: 'hidden',
              padding: 0,
            },
          },
        }}
      >
        <ScrollArea maxHeight="18.75rem">
          {columns
            .filter((col) => col.hideable !== false)
            .sort((a, b) => (a.headerName || a.field).localeCompare(b.headerName || b.field))
            .map((col) => {
              const isDisabledByCampaign = campaignColumnVisibility?.[col.field] === false;

              return (
                <Tooltip
                  key={col.field}
                  arrow
                  title={isDisabledByCampaign ? t('Campaign period is required') : ''}
                >
                  <span onClick={isDisabledByCampaign ? handleDisabledItemClick : undefined}>
                    <ColumnMenuItem
                      data-testid={`columns-menu-item-${col.field}`}
                      onClick={() => handleToggle(col.field)}
                      disabled={isDisabledByCampaign}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <ColumnCheckbox checked={visibleColumns[col.field] !== false}>
                          <CheckIcon />
                        </ColumnCheckbox>
                        {col.headerName || col.field}
                      </Stack>
                    </ColumnMenuItem>
                  </span>
                </Tooltip>
              );
            })}
        </ScrollArea>
      </Menu>
    </>
  );
};

export default ToolbarColumnsMenu;
